import LazyApexChart from "../../../client/components/LazyApexChart";
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { chartColors } from "../chart/chartColors";
import ChartViewToggle from "../ChartViewToggle";
import WidgetDataTable, { sampleSeriesToDaily } from "../WidgetDataTable";
import type { WidgetViewProps } from "../types";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

export default function DrawdownCurveWidget({ viewMode = "chart", onSetViewMode }: WidgetViewProps) {
  const { analyticsData, isDark, themeId, t } = useDashboardData();

  const tableColumns = [
    { key: "date", label: t.analytics.dateLabel, align: "left" as const },
    { key: "value", label: t.analytics.valueLabel, align: "right" as const },
  ];
  const tableRows = sampleSeriesToDaily(analyticsData?.drawdownCurve ?? []).map((p) => ({
    date: new Date(p.x).toLocaleDateString(undefined),
    value: (
      <span className={p.y >= 0 ? "text-pnlPositive" : "text-pnlNegative"}>
        {p.y >= 0 ? "+" : ""}
        {p.y.toFixed(2)}
      </span>
    ),
  }));

  if (!analyticsData || analyticsData.drawdownCurve.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardSectionHeader
          label={t.dashboard.widgets.drawdownCurve}
          isDark={isDark}
          compact
          rightContent={<ChartViewToggle value={viewMode} onChange={onSetViewMode} />}
        />
        <CardContent className="flex-1 p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  const curveColor = "hsl(var(--edgely-pnl-negative))";
  const c = chartColors();

  const chartOptions = {
    chart: {
      type: "area" as const,
      toolbar: { show: false },
      background: "transparent",
      animations: { enabled: false },
      foreColor: c.axis,
    },
    stroke: { curve: "smooth" as const, width: 2, colors: [curveColor] },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: isDark ? 0.35 : 0.25,
        opacityTo: 0.01,
        stops: [0, 100],
        colorStops: [
          { offset: 0, color: curveColor, opacity: isDark ? 0.35 : 0.25 },
          { offset: 100, color: curveColor, opacity: 0 },
        ],
      },
    },
    xaxis: {
      type: "datetime" as const,
      labels: {
        style: { colors: c.axis, fontSize: "11px" },
        datetimeUTC: false,
        format: "dd MMM",
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      tickAmount: 5,
      labels: {
        style: { colors: c.axis, fontSize: "11px" },
        formatter: (val: number) => {
          if (Math.abs(val) >= 1000) return `${(val / 1000).toFixed(1)}k`;
          return val.toFixed(0);
        },
      },
      forceNiceScale: true,
    },
    grid: {
      borderColor: c.grid,
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    tooltip: {
      theme: isDark ? "dark" : "light",
      x: { format: "dd MMM yyyy" },
      y: {
        formatter: (val: number) => `${val.toFixed(2)} $`,
        title: { formatter: () => t.analytics.peakToTrough },
      },
    },
    markers: { size: 0 },
    dataLabels: { enabled: false },
    legend: { show: false },
  };

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.dashboard.widgets.drawdownCurve}
        isDark={isDark}
        compact
        rightContent={<ChartViewToggle value={viewMode} onChange={onSetViewMode} />}
      />
      <CardContent className="flex-1 px-5 pb-5 pt-1">
        {viewMode === "table" ? (
          <WidgetDataTable
            columns={tableColumns}
            rows={tableRows}
            emptyLabel={t.analytics.noDataAvailable}
          />
        ) : (
          <LazyApexChart
            key={`drawdown-curve-widget-${themeId}`}
            options={chartOptions}
            series={[{ name: t.analytics.peakToTrough, data: analyticsData.drawdownCurve }]}
            type="area"
            height={240}
          />
        )}
      </CardContent>
    </Card>
  );
}
